export const formatDate = (dateString: string) => {
  if (!dateString) return '-';

  const datePart = dateString.substring(0, 10); // 2025-09-16
  const [year, month, day] = datePart.split('-');

  return `${day}/${month}/${year}`;
};

export const formatDateTime = (dateString: string) => {
  if (!dateString) return '-';

  const datePart = dateString.substring(0, 10); // 2025-09-16
  const timePart = dateString.substring(11, 16); // 03:00
  const [year, month, day] = datePart.split('-');

  return `${day}/${month}/${year} ${timePart}`;
};

export const formatStatusDetails = (
  status: string,
  paymentStatus: string | null | undefined,
  errorCode: string | null,
  errorMessage: string | null,
  nextActionUrl?: string | null,
) => {
  const normalizedPaymentStatus = (paymentStatus || 'unknown').toLowerCase();
  const paymentStatusDetails =
    normalizedPaymentStatus !== 'paid'
      ? `Payment status: ${getPaymentStatusDisplayValue(paymentStatus)}`
      : null;

  switch (status?.toLowerCase()) {
    case 'pending':
      return paymentStatusDetails || '-';

    case 'cancelled':
      return joinStatusDetails(errorMessage, paymentStatusDetails);

    case 'succeeded':
      return paymentStatusDetails || '-';

    case 'processing':
      return nextActionUrl
        ? joinStatusDetails('Awaiting client to complete 3DS verification', paymentStatusDetails)
        : paymentStatusDetails || '-';

    case 'failed':
      if (errorCode && errorMessage) {
        return joinStatusDetails(`Error ${errorCode} (${errorMessage})`, paymentStatusDetails);
      }
      if (errorCode) {
        return joinStatusDetails(`Error ${errorCode}`, paymentStatusDetails);
      }
      if (errorMessage) {
        return joinStatusDetails(`Error (${errorMessage})`, paymentStatusDetails);
      }
      return paymentStatusDetails || '-';

    default: {
      if (!errorCode && !errorMessage) return paymentStatusDetails || '-';
      if (errorCode && errorMessage) {
        return joinStatusDetails(`${errorCode}: ${errorMessage}`, paymentStatusDetails);
      }
      const fallbackDetails = errorCode || errorMessage || '-';
      if (fallbackDetails === '-') {
        return paymentStatusDetails || '-';
      }
      return joinStatusDetails(fallbackDetails, paymentStatusDetails);
    }
  }
};

export const getStatusDisplayValue = (status: string) => {
  switch (status?.toLowerCase()) {
    case 'cancelled':
      return 'Cancelled';
    case 'succeeded':
      return 'Succeeded';
    case 'processing':
      return 'Processing';
    case 'failed':
      return 'Failed';
    case 'timed out':
      return 'Timed Out';
    default:
      return status; // fallback to original value
  }
};

export const getSourceDisplayValue = (source: string) => {
  switch (source?.toLowerCase()) {
    case 'admin':
      return 'Ship Now (through Admin Panel)';
    case 'retry':
      return 'Automated (Payment Retry)';
    case 'automated':
      return 'Automated (First Attempt at Dispatch Date)';
    case 'account':
      return 'Ship Now (through Customer Dashboard)';
    default:
      return source; // fallback to original value
  }
};

export const getPaymentStatusDisplayValue = (paymentStatus?: string | null) => {
  if (!paymentStatus) return 'Unknown';

  const normalizedPaymentStatus = paymentStatus.toLowerCase();

  switch (normalizedPaymentStatus) {
    case 'paid':
      return 'Paid';
    case 'pending':
      return 'Pending';
    case 'partially_paid':
      return 'Partially Paid';
    case 'refunded':
      return 'Refunded';
    case 'partially_refunded':
      return 'Partially Refunded';
    case 'authorized':
      return 'Authorized';
    case 'voided':
      return 'Voided';
    case 'unknown':
      return 'Unknown';
    default:
      return normalizedPaymentStatus
        .replace(/_/g, ' ')
        .replace(/\b\w/g, (char) => char.toUpperCase());
  }
};

const joinStatusDetails = (
  baseMessage: string | null | undefined,
  paymentStatusDetails: string | null,
) => {
  const parts = [baseMessage, paymentStatusDetails].filter(Boolean);
  return parts.length > 0 ? parts.join('. ') : '-';
};

export const getShopifyOrderUrl = (orderId: number | null) => {
  if (!orderId) return null;
  return `https://admin.shopify.com/store/${import.meta.env.VITE_SHOPIFY_STORE}/orders/${orderId}`;
};
